import { Component, ErrorInfo, ReactNode } from "react";
import { SomethingWentWrong } from "../../components/SomethingWentWrong";
import { recordRumError } from "./recordRumError";
import { createXbLogger, XbLogger } from "./xbLogging";

export type XbErrorBoundaryProps = {
  componentName: string;
  /** Disable on outer/shell boundaries so pageLoad is not double-counted with inner route boundaries. Defaults to true. */
  trackPageLoad?: boolean;
  children: ReactNode;
};

type XbErrorBoundaryState = {
  hasError: boolean;
  componentName: string;
};

/**
 * The router reuses one instance across routes, so a componentName change is treated as a new page.
 */
export class XbErrorBoundary extends Component<XbErrorBoundaryProps, XbErrorBoundaryState> {
  public state: XbErrorBoundaryState = { hasError: false, componentName: this.props.componentName };
  private logger: XbLogger | null = null;

  private getLogger(): XbLogger {
    if (!this.logger) {
      this.logger = createXbLogger(this.props.componentName);
    }
    return this.logger;
  }

  private emitPageLoad(): void {
    if (this.props.trackPageLoad === false) {
      return;
    }
    this.getLogger().pageLoad();
  }

  public static getDerivedStateFromProps(
    props: XbErrorBoundaryProps,
    state: XbErrorBoundaryState
  ): Partial<XbErrorBoundaryState> | null {
    if (props.componentName === state.componentName) {
      return null;
    }
    return { hasError: false, componentName: props.componentName };
  }

  public static getDerivedStateFromError(): Partial<XbErrorBoundaryState> {
    return { hasError: true };
  }

  public componentDidMount(): void {
    this.emitPageLoad();
  }

  public componentDidUpdate(previousProps: XbErrorBoundaryProps): void {
    if (previousProps.componentName === this.props.componentName) {
      return;
    }
    this.logger = null;
    this.emitPageLoad();
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(`XbErrorBoundary(${this.props.componentName}) caught:`, error, errorInfo);
    recordRumError(error);
    this.getLogger().pageFailure();
  }

  public render(): ReactNode {
    if (this.state.hasError) {
      return <SomethingWentWrong />;
    }
    return this.props.children;
  }
}
